import { useInfiniteQuery, useQueries } from '@tanstack/react-query';
import { UseDataQueryOptions } from '../../../hooks/useDataQuery/dataQuery.types';
import useDataQuery from '../../../hooks/useDataQuery/useDataQuery';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';

import { getEnabledQueryBasedOnOfflineMode } from '../../../modules/offline/queryClient/utils/offlineDataQuery.utils';
import { GetProductDetailsProductColor } from '../../../modules/orders/products/interfaces/get-order-product-grid.interface';
import { ESProductSearch } from '../../../modules/products/ProductTypes';
import { GetProductDetailsEmptyAvailabilitiesOutput } from '../../../modules/products/products-details-v2/types/productDetails.types';
import {
  GC_TIME_2_MINUTES_IN_MS,
  GC_TIME_5_MINUTES_IN_MS,
  GC_TIME_30_SECONDS_IN_MS,
  STALE_TIME_2_MINUTES_IN_MS,
} from './productDetails.consts';
import {
  getCombinationsValidationKey,
  getProductDetailsCombinationGridKey,
  getProductDetailsCountGridKey,
  getProductDetailsEmptyAvailabilitiesKey,
  getProductDetailsFilterKey,
  getProductDetailsMediasKey,
  getProductDetailsProductColorKey,
  getProductDetailsSizesKey,
  getProductDetailsSummaryKey,
} from './productDetails.keys';
import productDetailsRepository from './productDetails.repository';
import {
  IProductDetailsCombinationsParams,
  IProductDetailsCount,
  IProductDetailsFilter,
  IProductDetailsGroupSectionParams,
  ProductDetailsByOrderIdsParams,
  ProductDetailsMedias,
  ProductDetailsMediasParams,
  ProductDetailsSize,
  ProductDetailsSummary,
  UseCombinationsValidationParams,
  UseGetProductDetailsSummaryParams,
} from './productDetails.types';

export const useProductDetailsCount = (
  params: IProductDetailsGroupSectionParams,
  options?: UseDataQueryOptions,
) => {
  return useDataQuery<IProductDetailsCount>(
    {
      queryKey: getProductDetailsCountGridKey(params),
      staleTime: STALE_TIME_2_MINUTES_IN_MS,
      gcTime: GC_TIME_5_MINUTES_IN_MS,
      queryFn: async () => {
        const {
          orderIds,
          catalogId,
          productId,
          availability,
          isAdminSeller,
          productColorId,
          deliveryWindowIds,
          allProductColorIds,
          filterByRelatedProducts,
        } = params;

        const response = await productDetailsRepository().getProductDetailsGroupSections({
          orderIds,
          catalogId,
          productId,
          availability,
          isAdminSeller,
          productColorId,
          deliveryWindowIds,
          allProductColorIds,
          filterByRelatedProducts,
        });

        if (response.ok && response.data) {
          const groups = response.data.map((section) => Number(section?.productsCount)) || [];
          return { groups, sections: response.data };
        }

        throw new Error('Erro ao buscar detalhes do produto.');
      },
    },

    options,
  );
};

export const useProductDetailsEmptyAvailabilities = (
  params: IProductDetailsGroupSectionParams,
  options?: UseDataQueryOptions,
) =>
  useDataQuery<GetProductDetailsEmptyAvailabilitiesOutput[] | undefined>(
    {
      queryKey: getProductDetailsEmptyAvailabilitiesKey(params),
      staleTime: STALE_TIME_2_MINUTES_IN_MS,
      gcTime: GC_TIME_5_MINUTES_IN_MS,
      queryFn: async () => {
        const { productId, orderId } = params;
        const response = await productDetailsRepository().getProductDetailsEmptyAvailabilities({
          productId,
          orderId,
        });

        if (response.ok && response.data) {
          return response.data;
        }
      },
    },

    options,
  );

export const useProductDetailsSizes = (
  params: IProductDetailsGroupSectionParams,
  options?: UseDataQueryOptions,
) =>
  useDataQuery<ProductDetailsSize[] | undefined>(
    {
      queryKey: getProductDetailsSizesKey(params),
      staleTime: STALE_TIME_2_MINUTES_IN_MS,
      gcTime: GC_TIME_5_MINUTES_IN_MS,
      queryFn: async () => {
        const { productId, orderId } = params;
        const response = await productDetailsRepository().getProductDetailsSizes({
          productId,
          orderId,
        });

        if (response.ok && response.data) {
          return response.data;
        }
      },
    },

    options,
  );

export const useProductDetailsSizesByOrderIds = ({
  productId,
  orderIds,
}: ProductDetailsByOrderIdsParams) => {
  const { team } = useApplicationContext();

  return useQueries({
    queries: orderIds.map((orderId) => ({
      queryKey: [team?.id, ...getProductDetailsSizesKey({ productId, orderId })],
      staleTime: STALE_TIME_2_MINUTES_IN_MS,
      gcTime: GC_TIME_5_MINUTES_IN_MS,
      refetchOnWindowFocus: false,
      enabled: !!productId,
      queryFn: async () => {
        const response = await productDetailsRepository().getProductDetailsSizes({
          productId,
          orderId,
        });

        if (response.ok && response.data) {
          return response.data;
        }

        return null;
      },
    })),
    combine: (results) => results.map((result) => result.data ?? null),
  });
};

export const useProductDetailsEmptyAvailabilitiesByOrderIds = ({
  productId,
  orderIds,
}: ProductDetailsByOrderIdsParams) => {
  const { team } = useApplicationContext();

  return useQueries({
    queries: orderIds.map((orderId) => ({
      queryKey: [team?.id, ...getProductDetailsEmptyAvailabilitiesKey({ productId, orderId })],
      gcTime: GC_TIME_5_MINUTES_IN_MS,
      refetchOnWindowFocus: false,
      enabled: !!productId && !!orderId,
      queryFn: async () => {
        const response = await productDetailsRepository().getProductDetailsEmptyAvailabilities({
          productId,
          orderId,
        });

        if (response.ok && response.data) {
          return response.data;
        }

        return null;
      },
    })),
    combine: (results) => results.map((result) => result.data ?? null),
  });
};

export const useProductDetailsFilter = (
  params: IProductDetailsGroupSectionParams,
  options?: UseDataQueryOptions,
) =>
  useDataQuery<IProductDetailsFilter | undefined>(
    {
      queryKey: getProductDetailsFilterKey(params),
      staleTime: STALE_TIME_2_MINUTES_IN_MS,
      gcTime: GC_TIME_5_MINUTES_IN_MS,
      keepPreviousData: true,
      queryFn: async () => {
        const response = await productDetailsRepository().getProductDetailsFilter(params);

        if (response.ok && response.data) {
          return response.data;
        }

        throw new Error('Erro ao buscar detalhes do produto.');
      },
    },

    options,
  );

export const useProductDetailsMedias = (
  params: ProductDetailsMediasParams,
  options?: UseDataQueryOptions,
) =>
  useDataQuery<ProductDetailsMedias | undefined>(
    {
      queryKey: getProductDetailsMediasKey(params),
      staleTime: STALE_TIME_2_MINUTES_IN_MS,
      gcTime: GC_TIME_5_MINUTES_IN_MS,
      keepPreviousData: true,
      queryFn: async () => {
        const response = await productDetailsRepository().getProductDetailsMedias(params);

        if (response.ok && response.data) {
          return response.data;
        }

        throw new Error('Erro ao buscar as mídias do produto.');
      },
    },

    options,
  );

export const useProductDetailsProductColor = (
  params: GetProductDetailsProductColor,
  options?: UseDataQueryOptions,
) =>
  useDataQuery<ESProductSearch | undefined>(
    {
      queryKey: getProductDetailsProductColorKey(params),
      staleTime: STALE_TIME_2_MINUTES_IN_MS,
      gcTime: GC_TIME_5_MINUTES_IN_MS,
      refetchOnMount: false,
      queryFn: async () => {
        const { productColorId, isAdminSeller, orderId } = params;
        const response = await productDetailsRepository().getProductDetailsProductColor({
          productColorId,
          isAdminSeller,
          orderId,
        });

        if (response.ok && response.data) {
          return response.data;
        }
      },
    },

    options,
  );

export const useProductDetailsCombinations = (
  params: IProductDetailsCombinationsParams,
  options?: UseDataQueryOptions,
) => {
  return useInfiniteQuery({
    queryKey: getProductDetailsCombinationGridKey(params),
    staleTime: STALE_TIME_2_MINUTES_IN_MS,
    gcTime: GC_TIME_2_MINUTES_IN_MS,
    refetchOnWindowFocus: false,
    enabled: getEnabledQueryBasedOnOfflineMode(params.isOfflineMode),
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const {
        limit,
        orderIds,
        productId,
        catalogId,
        availability,
        isAdminSeller,
        productColorId,
        deliveryWindowIds,
        allProductColorIds,
        selectedProductColorId,
        filterByRelatedProducts,
      } = params;

      try {
        const response = await productDetailsRepository(
          params.isOfflineMode,
        ).getProductDetailsCombinations({
          orderIds,
          productId,
          catalogId,
          availability,
          isAdminSeller,
          productColorId,
          deliveryWindowIds,
          allProductColorIds,
          selectedProductColorId,
          filterByRelatedProducts,
          limit,
          skip: pageParam,
        });

        return { ...response, prevOffSet: pageParam };
      } catch (_error) {
        throw new Error('Erro ao buscar detalhes do produto.');
      }
    },

    getNextPageParam: (lastPage) => {
      if (!lastPage.data?.length) {
        return undefined;
      }
      return lastPage.prevOffSet + params.limit;
    },
    ...options,
  });
};

export const useCombinationsValidation = ({
  queryKeyParams,
  perPageRequests,
  getRequests,
  isOfflineMode,
  options,
}: UseCombinationsValidationParams) =>
  useInfiniteQuery({
    queryKey: getCombinationsValidationKey(queryKeyParams),
    staleTime: STALE_TIME_2_MINUTES_IN_MS,
    gcTime: GC_TIME_30_SECONDS_IN_MS,
    refetchOnWindowFocus: false,
    enabled: !isOfflineMode && perPageRequests.length > 0,
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const request = getRequests()[pageParam];
      const response = await productDetailsRepository(isOfflineMode).getCombinationsValidation({
        orderIds: request.orderIds,
        combinations: request.combinations,
      });

      if (!response.ok || !response.data) {
        throw response;
      }

      return response.data;
    },
    getNextPageParam: (_lastPage, allPages) =>
      allPages.length < getRequests().length ? allPages.length : undefined,
    ...options,
  });

export const useGetProductDetailsSummary = ({
  params,
  options,
}: UseGetProductDetailsSummaryParams) => {
  return useDataQuery<ProductDetailsSummary>(
    {
      queryKey: getProductDetailsSummaryKey(params),
      staleTime: STALE_TIME_2_MINUTES_IN_MS,
      gcTime: GC_TIME_5_MINUTES_IN_MS,
      queryFn: async () => {
        const {
          orderId,
          productId,
          availability,
          productColorId,
          deliveryWindowIds,
          allProductColorIds,
          filterByRelatedProducts,
        } = params;

        const response = await productDetailsRepository().getProductDetailsSummary({
          orderId,
          productId,
          availability,
          productColorId,
          deliveryWindowIds,
          allProductColorIds,
          filterByRelatedProducts,
        });

        if (!response.ok || !response.data) {
          throw response.data;
        }

        return response.data;
      },
    },

    options,
  );
};
